import React from 'react';
import { Button, Form } from 'react-bootstrap';

// The statements that apply to an applicant: those for everyone, plus the
// ones for new or for returning students once they have said which they are.
export const applicableCommitments = (commitments, isReturning) =>
    (commitments || []).filter(
        ({ appliesTo }) =>
            appliesTo === 'all' ||
            (appliesTo === 'new' && isReturning === false) ||
            (appliesTo === 'returning' && isReturning === true)
    );

// The program's commitments, shown as a numbered list with one required
// checkbox. Nobody acknowledges a list they cannot see: while it loads, when
// it fails to load, and before the applicant has said whether they are new or
// returning, the checkbox is disabled and unticked.
//
// `requirements` is the result of useLARequirements: { requirements, loading,
// loadError, noTerm, retry }.
function ProgramCommitments({
    id,
    roleName,
    prompt,
    requirements,
    isReturning,
    checked,
    onChange,
    invalid,
    requiredNote = 'Required to submit.',
}) {
    const { loading, loadError, noTerm, retry } = requirements;
    const all = requirements.requirements?.studentCommitments;
    if (!loading && !loadError && !noTerm && !all?.length) return null;

    const listId = `${id}-list`;
    const errorId = `${id}-error`;
    const applicable = applicableCommitments(all, isReturning);
    const unavailable =
        loading || loadError || noTerm || typeof isReturning !== 'boolean';

    let message = null;
    if (loading) message = 'Loading the commitments';
    else if (loadError)
        message = 'The commitments could not be loaded right now.';
    else if (noTerm) message = 'The commitments are not available right now.';
    else if (typeof isReturning !== 'boolean') message = prompt;

    return (
        <fieldset className="mb-3">
            <legend className="h6">{roleName} commitments</legend>
            <div role="status">
                {message}
                {loadError && (
                    <>
                        {' '}
                        <Button variant="link" className="p-0" onClick={retry}>
                            Try again
                        </Button>
                    </>
                )}
            </div>
            <div id={listId} aria-live="polite">
                {!unavailable && (
                    <>
                        <p className="mb-2">
                            If you are hired as a {roleName}, you agree to the
                            following:
                        </p>
                        <ol>
                            {applicable.map(({ text }) => (
                                <li key={text}>{text}</li>
                            ))}
                        </ol>
                    </>
                )}
            </div>
            <Form.Check
                type="checkbox"
                id={id}
                label="I agree to each of the commitments above."
                checked={!unavailable && checked === true}
                disabled={unavailable}
                isInvalid={invalid}
                aria-required="true"
                aria-invalid={invalid ? 'true' : undefined}
                aria-describedby={invalid ? `${listId} ${errorId}` : listId}
                onChange={(e) => onChange(e.target.checked)}
            />
            <div className="text-muted small">{requiredNote}</div>
            {invalid && (
                <div
                    id={errorId}
                    role="alert"
                    className="invalid-feedback d-block"
                >
                    Acknowledgment is required.
                </div>
            )}
        </fieldset>
    );
}

export default ProgramCommitments;
